import _ from 'lodash';
import template from 'url-template';

import {
    CREATE_ERP_AGENT,
    ERP_AGENT_REORDER,
    FETCH_ERP_AGENTS,
    FETCH_MAPPED_ERP_CLIENTS,
    FETCH_MAPPED_ERP_TECHNICIANS,
    SET_CUSTOMER_TRACKING_LOADING,
    SET_ERP_AGENT,
    SET_ERP_AGENT_LOADING,
    SET_ERP_AGENTS_LOADING,
    SET_CUSTOMERS_LOADING,
    SET_CUSTOMER_SEARCH_VALUE,
    SET_TECHNICIANS_LOADING,
    SET_TECHNICIAN_TRACKING_LOADING,
    SET_TECHNICIAN_SEARCH_VALUE
} from './actionTypes';

import { handleNetworkError, enqueueSnackbar } from './applicationMessageActions';
import { setBobLoading } from './bobActions';

import awsHeader from '../helpers/awsAuth';
import lambdaBaseApi from '../apis/lambda';
import { getSortOrder } from '../helpers/collections';

const ErpAgentUrl = template.parse('/agentconfigurations/{id}');
const ErpAgentsUrl = template.parse('/agentconfigurations');
const RotateCredentialsUrl = template.parse('/agentconfigurations/{id}/rotatecredentials');
const ErpClientsUrl = template.parse('/externalclients{?agentConfigurationId,pageSize,page,searchFilter}');
const ErpTechniciansUrl = template.parse('/externaltechnicians{?agentConfigurationId,pageSize,page,searchFilter}');

export const createErpAgent = (companyId, agentData) => async dispatch => {
    const header = await awsHeader();

    try {
        const response = await lambdaBaseApi.post(
            ErpAgentsUrl.expand({}),
            agentData,
            header
        );
        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: CREATE_ERP_AGENT,
            payload
        });

        await dispatch(fetchErpAgents(companyId));
        dispatch(enqueueSnackbar('Agent created successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteErpAgent = (companyId, id) => async dispatch => {
    const header = await awsHeader();

    try {
        await lambdaBaseApi.delete(
            ErpAgentUrl.expand({ id }),
            header
        );

        await dispatch(fetchErpAgents(companyId));
        dispatch(enqueueSnackbar('Agent deleted successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const downloadErpAgentInstaller = () => async dispatch => {
    try {
        let env = process.env.REACT_APP_STAGE;

        if (env === 'stage') {
            env = 'live';
        }

        if (env === 'dev') {
            env = 'work';
        }

        if (env === 'prod') {
            env = 'io';
        }

        window.open(`https://s3.amazonaws.com/assets.key2act.${env}/signature/ERPAgent.exe`);
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchErpAgents = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        dispatch(setErpAgentsLoading(true));
        dispatch(setBobLoading('FetchErpAgents'));

        const response = await lambdaBaseApi.get(
            ErpAgentsUrl.expand({
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        const sortedAgents = _.sortBy(payload, 'name');

        dispatch({
            type:    FETCH_ERP_AGENTS,
            payload: sortedAgents
        });

        dispatch(setErpAgentsLoading(false));
        dispatch(setBobLoading('FetchErpAgents'));
    } catch (error) {
        dispatch(handleNetworkError(error));
        dispatch(setErpAgentsLoading(false));
        dispatch(setBobLoading('FetchErpAgents'));
    }
};

export const fetchErpAgent = (id, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await lambdaBaseApi.get(
            ErpAgentUrl.expand({
                id,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch(setAgent(payload));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const fetchMappedErpClients = (agentId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        dispatch(setCustomersLoading(true));

        const response = await lambdaBaseApi.get(
            ErpClientsUrl.expand({
                agentConfigurationId: agentId,
                ...options
            }),
            header
        );

        const data = _.get(response, 'data.data', []);
        const meta = _.get(response, 'data.meta', {});

        const payload = {
            data,
            meta
        };

        dispatch({
            type: FETCH_MAPPED_ERP_CLIENTS,
            payload
        });
        dispatch(setCustomersLoading(false));

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const updateErpClient = agentData => async dispatch => {
    const header = await awsHeader();
    dispatch(setCustomerTrackingLoading(true));

    try {
        await lambdaBaseApi.patch(
            ErpAgentsUrl.expand({}),
            agentData,
            header
        );
        await dispatch(fetchErpAgents());
        dispatch(setCustomerTrackingLoading(false));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchMappedErpTechnicians = (agentId, options = {}) => async dispatch => {
    const header = await awsHeader();

    try {
        dispatch(setTechniciansLoading(true));

        const response = await lambdaBaseApi.get(
            ErpTechniciansUrl.expand({
                agentConfigurationId: agentId,
                ...options
            }),
            header
        );
        const data = _.get(response, 'data.data', []);
        const meta = _.get(response, 'data.meta', {});

        const payload = {
            data,
            meta
        };

        dispatch({
            type: FETCH_MAPPED_ERP_TECHNICIANS,
            payload
        });
        dispatch(setTechniciansLoading(false));

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const rotateErpAgentCredentials = (id) => async (dispatch) => {
    const header = await awsHeader();
    try {
        dispatch(setErpAgentLoading(true));

        const response = await lambdaBaseApi.post(
            RotateCredentialsUrl.expand({ id }),
            {},
            header
        );
        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: CREATE_ERP_AGENT,
            payload
        });
        return true;
    } catch (error) {
        dispatch(setErpAgentLoading(false));
        dispatch(handleNetworkError(error));
    }
    finally {
        dispatch(setErpAgentLoading(false));
    }
};

export const setAgent = agent => ({
    type:    SET_ERP_AGENT,
    payload: agent
});

export const setCustomerSearch = payload => ({
    type: SET_CUSTOMER_SEARCH_VALUE,
    payload
});

export const setTechnicianSearch = payload => ({
    type: SET_TECHNICIAN_SEARCH_VALUE,
    payload
});

export const syncErpAgentData = (agentId) => async dispatch => {
    const header = await awsHeader();

    try {
        const body = {
            command: 'InventoryRefreshRequest',
            id:      agentId
        };

        await lambdaBaseApi.patch(
            ErpAgentsUrl.expand({}),
            body,
            header
        );
        dispatch(enqueueSnackbar('Inventory refresh requested.', 'success'));
    } catch (error) {
        dispatch(handleNetworkError(error));
        dispatch(enqueueSnackbar('Error syncing agent.', 'failure'));
    }
};

export const updateErpAgent = (companyId, id, body = {}) => async dispatch => {
    const header = await awsHeader();

    try {
        dispatch(setBobLoading('UpdateErpAgents'));

        const response = await lambdaBaseApi.put(
            ErpAgentsUrl.expand({
                id
            }),
            body,
            header
        );
        const payload = _.get(response, 'data.data', {});

        dispatch(setAgent(payload));
        await dispatch(fetchErpAgents(companyId));

        dispatch(enqueueSnackbar('Agent updated successfully.', 'success'));
        dispatch(setBobLoading('UpdateErpAgents'));
        return true;
    } catch (error) {
        dispatch(setBobLoading('UpdateErpAgents'));
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const updateErpTechnician = agentData => async dispatch => {
    const header = await awsHeader();
    dispatch(setTechnicianTrackingLoading(true));

    try {
        await lambdaBaseApi.patch(
            ErpAgentsUrl.expand({}),
            agentData,
            header
        );
        await dispatch(fetchErpAgents());
        dispatch(setTechnicianTrackingLoading(false));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const clearNewAgent = () => ({
    type:    CREATE_ERP_AGENT,
    payload: {}
});

export const reorderErpAgents = (newOrderBy) => async (dispatch, getState) => {
    const currentState = getState().erpAgent;
    const order = getSortOrder(newOrderBy, currentState);
    dispatch({
        type:    ERP_AGENT_REORDER,
        payload: {
            order,
            orderBy: newOrderBy
        }
    });
};

export const setErpAgents = payload => async (dispatch, getState) => {
    dispatch({
        type: FETCH_ERP_AGENTS,
        payload
    });
};

export const setErpAgentLoading = payload => ({
    type: SET_ERP_AGENT_LOADING,
    payload
});

export const setCustomerTrackingLoading = payload => ({
    type: SET_CUSTOMER_TRACKING_LOADING,
    payload
});


export const setErpAgentsLoading = payload => ({
    type: SET_ERP_AGENTS_LOADING,
    payload
});

export const setCustomersLoading = payload => ({
    type: SET_CUSTOMERS_LOADING,
    payload
});

export const setTechniciansLoading = payload => ({
    type: SET_TECHNICIANS_LOADING,
    payload
});

export const setTechnicianTrackingLoading = payload => ({
    type: SET_TECHNICIAN_TRACKING_LOADING,
    payload
});
